/**
 * Bridge Service Storage Client
 * Saves captured photos to local disk via Bridge and sends to Print Server
 */

import { getEffectiveBridgeBaseUrl } from '../config/terminal-urls';

export interface SavePhotoResponse {
  success: boolean;
  path: string;
  folderName: string;
}

export interface SendFolderResponse {
  success: boolean;
  message: string;
}

/**
 * Save photo to Bridge storage (local disk).
 * For original photos: YYYY-MM-DD-customerName-transactionId/sequence.jpg
 * For edited photos: {originalFolder}/edited/{sequence}_{timestamp}.jpg
 */
export async function savePhotoToBridge(
  imageBlob: Blob,
  transactionId: string,
  customerName: string,
  sequence: number,
  isEdited: boolean = false
): Promise<SavePhotoResponse> {
  const form = new FormData();
  form.append('photo', imageBlob, `${sequence}.jpg`);
  form.append('transactionId', transactionId);
  form.append('customerName', customerName);
  form.append('sequence', String(sequence));
  form.append('isEdited', String(isEdited));

  const baseUrl = getEffectiveBridgeBaseUrl();

  const response = await fetch(`${baseUrl}/storage/photos`, {
    method: 'POST',
    body: form,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(err.message || 'Failed to save photo to bridge');
  }

  return response.json();
}

/**
 * Send folder to Print Server via Bridge.
 * Requires BRIDGE_PRINT_SERVER_URL to be configured on Bridge side.
 */
export async function sendFolderToPrintServer(
  folderName: string
): Promise<SendFolderResponse> {
  const baseUrl = getEffectiveBridgeBaseUrl();

  const response = await fetch(`${baseUrl}/storage/send`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ folderName }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(err.message || 'Failed to send folder to Print Server');
  }

  return response.json();
}

/**
 * Get folder name for a transaction (matches Bridge format).
 * Format: YYYY-MM-DD-customerName-transactionId
 */
export function getStorageFolderName(
  transactionId: string,
  customerName: string
): string {
  const date = new Date();
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const sanitized = customerName
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[\/\\:*?"<>|]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'unknown';
  return `${yyyy}-${mm}-${dd}-${sanitized}-${transactionId}`;
}
